import type { Fetcher } from '@/lib/api/fetcher';

import type { Device, MobileConfig } from './schemas';
import { deviceListSchema, deviceSchema, mobileConfigSchema } from './schemas';

/**
 * BP-11 §5's device registry, over the shared transport.
 *
 * **No idempotency keys.** `POST /me/devices` is rule R4's upsert on the
 * token — posting the same registration twice updates one row and is
 * exactly what a token refresh does — and `DELETE` of a device already
 * gone is a 404 the caller ignores. Neither creates anything, which is the
 * test BP-06's payments fail and these pass.
 */

/** What the app sends when it learns a token (R4). */
export interface DeviceRegistration {
  platform: 'android' | 'ios';
  token: string;
  app_version?: string;
  locale?: string;
}

/** Every §5 device call the client app makes. */
export interface DevicesApi {
  listDevices: () => Promise<Device[]>;
  registerDevice: (registration: DeviceRegistration) => Promise<Device>;
  forgetDevice: (id: string) => Promise<void>;
  readMobileConfig: () => Promise<MobileConfig>;
}

/** Bind the §5 device routes to a transport. */
export function createDevicesApi(fetcher: Fetcher): DevicesApi {
  return {
    listDevices: () => fetcher.json('/v1/me/devices', { method: 'GET' }, deviceListSchema),
    registerDevice: (registration) =>
      fetcher.json('/v1/me/devices', { method: 'POST', body: registration }, deviceSchema),
    forgetDevice: (id) => fetcher.empty(`/v1/me/devices/${id}`, { method: 'DELETE' }),
    readMobileConfig: () =>
      fetcher.json('/v1/mobile/config', { method: 'GET' }, mobileConfigSchema),
  };
}
